import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { MaintenanceMessage } from '@dhl/maintenance'
import { Box, CircularProgress, Container, Grid, Sx, useTheme } from '@dhl/theme'

import { Basket } from '~/basket/model/reducers'
import { NavigatorDesktopShipment } from '~/basket/view/components/Navigator/NavigatorDesktopShipment'
import { Title } from '~/components/Title'
import { getConfig } from '~/config'
import { onOtcPath } from '~/paths'

import { StepNavigation } from '../withBasket'
import { BottomNavigator } from './BottomNavigator'
import { NavigatorDesktop } from './NavigatorDesktop'
import { Stepper } from './Stepper/Stepper'

const classes: Record<string, Sx> = {
  root: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  progress: {
    zIndex: 30,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: '-20px',
    marginLeft: '-20px',
  },
}

type Props = {
  isLoadingBasket?: boolean
  showNavigation?: boolean
  showTotalPrice?: boolean
  testId?: string
  basket?: Basket
  stepNavigation: StepNavigation
  children: ReactNode
}

export const ShipmentTemplate = ({
  isLoadingBasket = false,
  stepNavigation,
  showNavigation,
  showTotalPrice,
  basket,
  testId,
  children,
}: Props) => {
  const {
    device: { isMobile, isDesktop },
  } = useTheme()
  const { t } = useTranslation()
  const { prismic } = getConfig()

  return (
    <Container data-test={isLoadingBasket ? '' : testId} maxWidth="lg" sx={classes.root}>
      <MaintenanceMessage
        prismicEndpoint={prismic.endpoint}
        sx={(theme) => ({
          mb: 3,
          width: theme.device.isDesktop ? '90%' : 'unset',
        })}
        tag={onOtcPath() ? 'otc' : 'c2c'}
      />

      <Grid container wrap="wrap" direction="row" alignItems="stretch">
        <Grid size={{ lg: 9, xs: 12 }}>
          <Box m={1}>
            <Stepper stepNavigation={stepNavigation} />
          </Box>

          <Title>{t(stepNavigation.displayedStep)}</Title>

          <Box mb={isMobile ? 8 : 0}>{children}</Box>

          {showNavigation && (
            <>
              {isMobile && (
                <Box display="flex" position="fixed" left="0" width="100%" zIndex="appBar">
                  <BottomNavigator
                    stepNavigation={stepNavigation}
                    showTotalPrice={showTotalPrice || false}
                    isLoadingBasket={isLoadingBasket}
                    basket={basket}
                  />
                </Box>
              )}
              {isDesktop && (
                <NavigatorDesktop
                  stepNavigation={stepNavigation}
                  disabled={isLoadingBasket || basket?.isSubmitting || false}
                />
              )}
            </>
          )}
        </Grid>
        <Grid size={{ sm: 3 }}>
          {showNavigation && basket && isDesktop && (
            <NavigatorDesktopShipment
              stepNavigation={stepNavigation}
              showTotalPrice={showTotalPrice || false}
              isLoadingBasket={isLoadingBasket}
              basket={basket}
            />
          )}
        </Grid>
      </Grid>
      {isLoadingBasket && (
        <Box sx={classes.progress}>
          <CircularProgress />
        </Box>
      )}
    </Container>
  )
}
